import { useEffect, useRef, useState } from "react";
import { Radio, X, Send, Loader2, LogOut, StopCircle, Users } from "lucide-react";
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
} from "@livekit/components-react";
import "@livekit/components-styles";
import api, { formatError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useLive } from "@/contexts/LiveContext";

/**
 * LiveRoomModal — the actual "live room". V2 ships **real cam + mic**
 * via LiveKit Cloud on top of the existing presence + chat + emoji
 * reaction layer.
 *
 * Layout (responsive):
 *   • Header — LIVE pill, title, joined count, close button
 *   • LiveKit video stage — full grid of cam tiles + mic/cam/leave bar
 *   • Family chat strip — our existing chat thread + emoji bar +
 *     composer, pinned to the bottom 38% of the modal so faces stay
 *     visible while people chat.
 *
 * Graceful degradation: if `/api/live/{id}/token` returns
 * `{enabled: false}` (i.e. LIVEKIT_* env vars unset), we hide the video
 * stage and run the modal in chat-only mode — exactly the V1 behaviour.
 */
const REACTIONS = ["👍", "❤️", "🔥", "💪", "😂", "👏", "🙌", "🎉", "😮"];
const POLL_MS = 2500;

export default function LiveRoomModal() {
  const { user } = useAuth();
  const { activeRoomId, closeRoom, refresh } = useLive();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [ending, setEnding] = useState(false);
  // LiveKit video state. `lkEnabled` flips to false if the backend
  // returns {enabled: false} so we can degrade gracefully to chat-only.
  const [lkToken, setLkToken] = useState(null);
  const [lkServerUrl, setLkServerUrl] = useState(null);
  const [lkEnabled, setLkEnabled] = useState(true);
  const cursorRef = useRef(null);
  const listEndRef = useRef(null);
  const pollRef = useRef(null);

  const isHost = session && user && session.host_user_id === user.user_id;

  // Open lifecycle: join the session, then load + poll chat. Cleanup on close.
  useEffect(() => {
    if (!activeRoomId) return;
    let cancelled = false;
    setErr("");
    setMessages([]);
    setLkToken(null);
    setLkServerUrl(null);
    setLkEnabled(true);
    cursorRef.current = null;

    (async () => {
      try {
        const { data } = await api.post(`/live/${activeRoomId}/join`);
        if (!cancelled) setSession(data);
      } catch (e) {
        if (!cancelled) setErr(formatError(e));
        return;
      }
      // Fetch a LiveKit token so we can spin up the cam grid. If LiveKit
      // isn't configured the backend replies {enabled:false}; we just hide
      // the video stage and run chat-only.
      try {
        const { data } = await api.post(`/live/${activeRoomId}/token`);
        if (cancelled) return;
        if (data?.enabled && data?.token && data?.server_url) {
          setLkToken(data.token);
          setLkServerUrl(data.server_url);
        } else {
          setLkEnabled(false);
        }
      } catch (e) {
        if (!cancelled) setLkEnabled(false);
        console.warn("[live-room] livekit token fetch failed:", e?.message || e);
      }
    })();

    const tick = async () => {
      try {
        const params = cursorRef.current ? { since: cursorRef.current } : {};
        const { data } = await api.get(`/live/${activeRoomId}/chat`, { params });
        const items = data?.items || [];
        if (items.length) {
          setMessages((prev) => [...prev, ...items]);
          cursorRef.current = items[items.length - 1].created_at;
        }
      } catch (e) {
        // Soft-fail — the polling loop will retry on the next interval.
        console.warn("[live-room] chat poll failed:", e?.message || e);
      }
    };
    // First fetch immediately so the host can see chat history when re-opening.
    tick();
    pollRef.current = setInterval(tick, POLL_MS);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeRoomId]);

  // Auto-scroll the chat thread to the newest message.
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  if (!activeRoomId) return null;

  const sendChat = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setErr("");
    try {
      const { data } = await api.post(`/live/${activeRoomId}/chat`, { text: trimmed });
      setMessages((prev) => [...prev, data]);
      cursorRef.current = data.created_at;
      setText("");
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setSending(false);
    }
  };

  const sendReaction = async (emoji) => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/live/${activeRoomId}/chat`, { emoji });
      setMessages((prev) => [...prev, data]);
      cursorRef.current = data.created_at;
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setSending(false);
    }
  };

  const leave = async () => {
    try {
      await api.post(`/live/${activeRoomId}/leave`);
    } catch (e) {
      // Best-effort: closing the room locally is more important than the
      // server-side leave write. Log it so we can spot a regression in dev.
      console.warn("[live-room] leave failed:", e?.message || e);
    }
    closeRoom();
    refresh && refresh();
  };

  const endLive = async () => {
    if (!isHost) return;
    if (!window.confirm("End this live session for everyone?")) return;
    setEnding(true);
    try {
      await api.post(`/live/${activeRoomId}/stop`);
      closeRoom();
      refresh && refresh();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setEnding(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center p-0 sm:p-4"
      style={{ background: "rgba(0,0,0,0.65)" }}
      data-testid="live-room-modal"
      onClick={leave}
    >
      <div
        className="bd-surface w-full sm:max-w-4xl flex flex-col"
        style={{
          maxHeight: "92vh",
          height: "100%",
          borderRadius: "var(--radius-card)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-4 py-3"
          style={{
            background: "linear-gradient(90deg, #ff3b30, #ff2d92)",
            color: "#fff",
          }}
        >
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bd-mono"
            style={{
              background: "rgba(0,0,0,0.25)",
              fontSize: "0.65rem",
              letterSpacing: "0.08em",
              fontWeight: 800,
              animation: "bd-live-dot 1.2s ease-in-out infinite",
            }}
          >
            <Radio size={11} /> LIVE
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate" data-testid="live-room-title">
              {session?.title || "Live session"}
            </div>
            <div className="text-xs opacity-90 truncate">
              Hosted by {session?.host_name || "—"}
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1 text-xs"
            style={{ opacity: 0.9 }}
            title="People who have joined"
            data-testid="live-room-joined-count"
          >
            <Users size={12} /> {session?.joined_user_ids?.length || 0}
          </span>
          <button
            onClick={leave}
            style={{ background: "transparent", color: "#fff", border: "none", cursor: "pointer" }}
            data-testid="live-room-close"
            aria-label="Leave live"
          >
            <X size={20} />
          </button>
        </div>

        {/* LiveKit video stage — real cam + mic. Hidden when LiveKit
            isn't configured so chat-only mode still works locally. */}
        {lkEnabled && (
          <div
            className="relative flex-1 min-h-0"
            style={{ background: "#000", minHeight: "260px" }}
            data-testid="live-room-video-stage"
          >
            {lkToken && lkServerUrl ? (
              <div data-lk-theme="default" style={{ width: "100%", height: "100%" }}>
                <LiveKitRoom
                  serverUrl={lkServerUrl}
                  token={lkToken}
                  connect={true}
                  video={true}
                  audio={true}
                  onDisconnected={() => {
                    // When LiveKit drops us (user clicked Leave inside the
                    // VideoConference toolbar, or net dropped) close our
                    // modal so we don't leave a ghost chat shell behind.
                    leave();
                  }}
                  style={{ width: "100%", height: "100%" }}
                  data-testid="livekit-room"
                >
                  <VideoConference />
                  <RoomAudioRenderer />
                </LiveKitRoom>
              </div>
            ) : (
              <div
                className="absolute inset-0 grid place-items-center text-sm"
                style={{ color: "rgba(255,255,255,0.85)" }}
                data-testid="live-room-video-loading"
              >
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Starting video…
                </div>
              </div>
            )}
          </div>
        )}

        {/* Body — chat thread */}
        <div
          className={lkEnabled ? "overflow-y-auto px-4 py-3 flex flex-col gap-2" : "flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-2"}
          style={{
            background: "var(--bg-soft)",
            ...(lkEnabled ? { maxHeight: "32vh" } : {}),
          }}
          data-testid="live-room-chat"
        >
          {messages.length === 0 && (
            <div
              className="text-center text-sm py-10"
              style={{ color: "var(--text-dim)" }}
              data-testid="live-room-empty"
            >
              Say hi — react with an emoji or drop a message to cheer them on.
            </div>
          )}
          {messages.map((m) => {
            const mine = m.user_id === user?.user_id;
            const isReaction = m.kind === "reaction";
            return (
              <div
                key={m.msg_id}
                className={`flex items-start gap-2 ${mine ? "flex-row-reverse" : ""}`}
                data-testid={`live-msg-${m.msg_id}`}
              >
                <Avatar
                  name={m.user_name}
                  picture={m.user_picture}
                  size={24}
                />
                <div
                  className="max-w-[75%]"
                  style={{
                    background: mine ? "var(--primary)" : "var(--bg)",
                    color: mine ? "var(--primary-fg)" : "var(--text)",
                    padding: isReaction ? "0.25rem 0.55rem" : "0.4rem 0.7rem",
                    borderRadius: "14px",
                    border: "1px solid var(--border)",
                    fontSize: isReaction ? "1.4rem" : "0.85rem",
                    lineHeight: 1.25,
                    wordBreak: "break-word",
                  }}
                >
                  {!isReaction && !mine && (
                    <div className="text-[10px] font-semibold mb-0.5" style={{ opacity: 0.7 }}>
                      {m.user_name}
                    </div>
                  )}
                  {isReaction ? m.emoji : m.text}
                </div>
              </div>
            );
          })}
          <div ref={listEndRef} />
        </div>

        {/* Emoji reaction bar */}
        <div
          className="px-3 py-2 flex items-center gap-1 overflow-x-auto"
          style={{ borderTop: "1px solid var(--border)", background: "var(--bg)" }}
          data-testid="live-room-reaction-bar"
        >
          {REACTIONS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => sendReaction(e)}
              disabled={sending}
              className="bd-chip"
              style={{
                fontSize: "1.1rem",
                padding: "0.25rem 0.55rem",
                background: "transparent",
              }}
              data-testid={`live-room-react-${e}`}
              aria-label={`React with ${e}`}
            >
              {e}
            </button>
          ))}
        </div>

        {/* Composer + host controls */}
        <div className="px-3 py-3 flex items-center gap-2" style={{ borderTop: "1px solid var(--border)" }}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
            placeholder="Say something…"
            className="bd-input flex-1"
            style={{ padding: "0.55rem 0.8rem" }}
            data-testid="live-room-input"
            maxLength={500}
          />
          <button
            type="button"
            onClick={sendChat}
            disabled={sending || !text.trim()}
            className="bd-btn"
            style={{ padding: "0.55rem 0.8rem" }}
            data-testid="live-room-send"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>

        {(err) && (
          <div
            className="px-3 py-2 text-xs"
            style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }}
            data-testid="live-room-error"
          >
            {err}
          </div>
        )}

        <div className="px-3 py-2 flex items-center justify-between" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            type="button"
            onClick={leave}
            className="bd-btn bd-btn-ghost text-xs"
            style={{ padding: "0.35rem 0.7rem" }}
            data-testid="live-room-leave"
          >
            <LogOut size={12} /> Leave
          </button>
          {isHost && (
            <button
              type="button"
              onClick={endLive}
              disabled={ending}
              className="bd-btn"
              style={{
                padding: "0.35rem 0.8rem",
                fontSize: "0.75rem",
                background: "#ff3b30",
                color: "#fff",
                borderColor: "#ff3b30",
              }}
              data-testid="live-room-end"
            >
              {ending ? <Loader2 size={12} className="animate-spin" /> : <><StopCircle size={12} /> End live</>}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes bd-live-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
    </div>
  );
}
